import React, { FC, useState, useEffect, memo, useMemo } from 'react';
import FormControl from '@material-ui/core/FormControl';
import Typography from '@material-ui/core/Typography';

import { Checkbox } from '@keaze/web/components/checkbox';
import { FilterData, FilterProps } from '../filters.types';
import { Count } from './checkboxes.styles';

type IsChecked = (currentValue: number) => boolean;

type OnChange = (currentValue: number, checked: boolean) => void;

type GenerateLabel = (text: string, count: number) => JSX.Element;

type GenerateInitialState = (value: number[], data: FilterData[]) => number[];

const generateLabel: GenerateLabel = (text, count) => (
  <>
    <Typography variant="body2" color="textPrimary">
      {text}
    </Typography>
    {!!count && (
      <Count variant="body2" color="textPrimary">
        {count}
      </Count>
    )}
  </>
);

const generateInitialState: GenerateInitialState = (value, data) =>
  value.filter((selectedCheckboxValue) => {
    const currentCheckbox = data.find(
      ({ value }) => value === selectedCheckboxValue
    );

    return currentCheckbox?.count !== 0;
  });

export const Checkboxes: FC<FilterProps<number[]>> = memo(
  ({ name, value = [], data, onChange }) => {
    const initialValue = useMemo(() => generateInitialState(value, data), [
      value,
      data,
    ]);
    const [state, setState] = useState<number[]>(initialValue);

    useEffect(() => {
      setState(initialValue);
    }, [initialValue]);

    const isChecked: IsChecked = (currentValue) =>
      state.some((value) => value === currentValue);

    const handleChange: OnChange = (currentValue, checked) => {
      if (!checked) {
        return setState((prevState) => {
          const newState = prevState.filter((value) => value !== currentValue);

          onChange(name, newState);
          return newState;
        });
      }

      return setState((prevState) => {
        const newState = [...prevState, currentValue];

        onChange(name, newState);
        return newState;
      });
    };

    return (
      <FormControl fullWidth component="fieldset">
        {data.map(({ text, value, count }) => (
          <Checkbox
            key={value}
            name={name}
            label={generateLabel(text, Number(count))}
            value={value}
            disabled={count === 0}
            onChange={(e, checked) => handleChange(Number(value), checked)}
            checked={isChecked(Number(value))}
          />
        ))}
      </FormControl>
    );
  }
);
